import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, filter } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { isPresent } from 'src/app/core/utils/isPresent';
import {
  DocumentProcessingPagedReviewResponse,
  FlagType,
  ShortReviewClauseResponseItem,
} from 'src/app/modules/documents/services/document-processing-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { WorkspaceDetailsDto, WorkspaceDocumentDto } from '../../../../models/workspaces.model';
import {
  selectAnalyzingClauseVariance,
  selectAnalyzingClauseVarianceResponse,
  selectLoadingReviewDataComplete,
  selectNonReferenceDocuments,
  selectPagedReviewDataByDocumentIdMap,
  selectReferenceDocument,
  selectReviewDoneWithErrors,
  selectWorkspaceDetails,
} from '../../../../store/selectors';

@UntilDestroy()
@Component({
  selector: 'app-analyzing-clause-alignment-matrix',
  templateUrl: './analyzing-clause-alignment-matrix.component.html',
  styleUrl: './analyzing-clause-alignment-matrix.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyzingClauseAlignmentMatrixComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public isAnalyzingClauseVarianceLoading$ = this.store$.select(selectAnalyzingClauseVariance);
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public selectedReferenceDocument: WorkspaceDocumentDto | null = null;
  public reviewDocuments: WorkspaceDocumentDto[] = [];
  public clauseAlignmentRows: ClauseAlignmentRowViewModel[] = [];
  public matrixGridTemplateColumns = 'minmax(240px, 2fr)';
  public loadingReviewDataComplete = false;
  public reviewDoneWithErrors = false;
  public constants = constants;
  public ClauseAlignmentStatus = ClauseAlignmentStatus;

  constructor(
    private readonly store$: Store,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.startInitialization();
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails).pipe(untilDestroyed(this), filter(isPresent)),
      this.store$.select(selectAnalyzingClauseVarianceResponse).pipe(untilDestroyed(this)),
      this.store$.select(selectReferenceDocument).pipe(untilDestroyed(this)),
      this.store$.select(selectNonReferenceDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectPagedReviewDataByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectLoadingReviewDataComplete).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewDoneWithErrors).pipe(untilDestroyed(this)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          workspaceDetails,
          analyzingClauseVarianceResponse,
          selectedReferenceDocument,
          nonReferenceDocuments,
          pagedReviewDataByDocumentId,
          loadingReviewDataComplete,
          reviewDoneWithErrors,
        ]) => {
          
          this.workspaceDetails = workspaceDetails;
          this.selectedReferenceDocument = selectedReferenceDocument;
          this.reviewDocuments = nonReferenceDocuments ?? [];
          this.matrixGridTemplateColumns = this.getMatrixGridTemplateColumns(
            this.reviewDocuments.length,
          );
          this.clauseAlignmentRows = this.getClauseAlignmentRows(
            analyzingClauseVarianceResponse?.playbook?.clauses ?? [],
            this.reviewDocuments,
            pagedReviewDataByDocumentId,
          );
          this.loadingReviewDataComplete = loadingReviewDataComplete;
          this.reviewDoneWithErrors = reviewDoneWithErrors;
          this.cdr.markForCheck();
        },
      );
  }

  private getMatrixGridTemplateColumns(documentCount: number): string {
    return `minmax(0, 2fr) repeat(${documentCount}, minmax(0, 1fr))`;
  }

  private getClauseAlignmentRows(
    playbookClauses: ShortReviewClauseResponseItem[],
    reviewDocuments: WorkspaceDocumentDto[],
    pagedReviewDataByDocumentId: Record<string, DocumentProcessingPagedReviewResponse>,
  ): ClauseAlignmentRowViewModel[] {
    return playbookClauses
      .map((clause) => ({
        clauseId: clause.id,
        clauseName: clause.name,
        documentStatuses: reviewDocuments.map((document) => ({
          documentId: document.id,
          documentName: document.name,
          status: this.getClauseAlignmentStatus(
            clause.id,
            pagedReviewDataByDocumentId[document.id],
          ),
        })),
      }))
      .filter((row) =>
        row.documentStatuses.some((documentStatus) => documentStatus.status !== null),
      );
  }

  private getClauseAlignmentStatus(
    clauseId: number,
    pagedReviewData?: DocumentProcessingPagedReviewResponse,
  ): ClauseAlignmentStatus | null {
    const matchingClauses =
      pagedReviewData?.clauses.filter((clause) => clause.id === clauseId) ?? [];

    if (!matchingClauses.length) {
      return null;
    }

    return matchingClauses.some((clause) => clause.flagType === FlagType.MostDesired)
      ? ClauseAlignmentStatus.Yes
      : ClauseAlignmentStatus.No;
  }
}

export enum ClauseAlignmentStatus {
  Yes = 'yes',
  No = 'no',
  None = 'none',
}

export interface ClauseAlignmentRowViewModel {
  clauseId: number;
  clauseName: string;
  documentStatuses: ClauseAlignmentDocumentStatusViewModel[];
}

export interface ClauseAlignmentDocumentStatusViewModel {
  documentId: string;
  documentName: string;
  status: ClauseAlignmentStatus | null;
}
